/**
 * When Menu can reach the extension, drain captured productview JSON from the
 * background queue and persist history + structured cogo_articles rows.
 */

import { cogoCommand } from './cogoExtension'

const POLL_MS = 4_000
const DRAIN_LIMIT = 80

let running = false
let abort: AbortController | null = null

function sleep(ms: number, signal: AbortSignal) {
  return new Promise<void>((resolve) => {
    const t = setTimeout(resolve, ms)
    signal.addEventListener(
      'abort',
      () => {
        clearTimeout(t)
        resolve()
      },
      { once: true }
    )
  })
}

async function drainOnce(): Promise<number> {
  const res = await cogoCommand(
    { type: 'drainProductviews', limit: DRAIN_LIMIT },
    8_000
  )
  if (res.status === 'timeout' || res.ok === false) return 0
  const items = Array.isArray(res.items)
    ? (res.items as Array<{
        technicalProductId?: string
        payload?: Record<string, unknown>
        source?: string | null
        sourceUrl?: string | null
        searchTerm?: string | null
      }>)
    : []
  if (items.length === 0) return 0

  const entries = items
    .filter((i) => i?.payload && typeof i.payload === 'object')
    .map((i) => ({
      technicalProductId:
        i.technicalProductId != null ? String(i.technicalProductId) : '',
      payload: i.payload as never,
      source: i.source ?? null,
      sourceUrl: i.sourceUrl ?? null,
      searchTerm: i.searchTerm ?? null,
    }))
    .filter((e) => e.technicalProductId)

  if (entries.length === 0) return 0

  try {
    const post = await fetch('/api/cogo/productview/ingest', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ entries }),
    })
    if (!post.ok) throw new Error(`ingest ${post.status}`)
    return entries.length
  } catch {
    // Put captures back so a later poll can retry.
    await cogoCommand(
      {
        type: 'productviewCaptured',
        products: entries.map((e) => ({
          technicalProductId: e.technicalProductId,
          raw: e.payload,
          source: e.source,
          sourceUrl: e.sourceUrl,
          searchTerm: e.searchTerm,
        })),
      },
      5_000
    )
    return 0
  }
}

async function loop(signal: AbortSignal) {
  while (!signal.aborted) {
    try {
      let drained = await drainOnce()
      // Keep draining while the extension queue is hot.
      while (!signal.aborted && drained >= DRAIN_LIMIT) {
        drained = await drainOnce()
      }
    } catch {
      /* extension absent / network — retry next tick */
    }
    if (signal.aborted) break
    await sleep(POLL_MS, signal)
  }
}

export function startCogoProductviewIngestWorker() {
  if (running) return
  running = true
  abort = new AbortController()
  void loop(abort.signal)
}

export function stopCogoProductviewIngestWorker() {
  running = false
  abort?.abort()
  abort = null
}
